import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import endpoints from '../../../config/endpoints';
import { useResponsive } from '../../../hooks/useResponsive';
import { Sidebar } from '../../../layouts/Sidebar';
import { setLoading } from '../../../services/reducers/homeSlice';
import restApi from '../../../services/restApi';
import { IAddress, IDistrict, IService, IUser, IWorkerOff } from '../../../types/types';
import { formatWorkerOffs } from '../../../utils/formatters';
import tools from '../../../utils/tools';
import AddressesSection from './AddressesSection';
import BankInfoSection from './BankInfoSection';
import OrdersSection from './OrdersSection';
import UserImageSection from './UserImageSection';
import UserInfoSection from './UserInfoSection';
import WorkerOffsSection from './WorkerOffsSection';
const sections = [
  { id: 'info', title: 'اطلاعات کاربر', icon: '👤' },
  { id: 'addresses', title: 'آدرس ها', icon: '📍' },
  { id: 'orders', title: 'سفارش ها', icon: '📦' },
  { id: 'bank', title: 'اطلاعات بانکی', icon: '🏦' },
  { id: 'image', title: 'تصویر', icon: '📷' },
  { id: 'schedule', title: 'تایم های مشغولی', icon: '📅' },
];
const UserManage: React.FC = () => {
  const { id: paramId } = useParams<{
    id: string
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [workerOffs, setWorkerOffs] = useState<Record<string, IWorkerOff[]>>({});
  const [user, setUser] = useState<IUser>({} as IUser);
  const [services, setServices] = useState<IService[]>([]);
  const [districts, setDistricts] = useState<IDistrict[]>([]);
  const [selectedWorkerServices, setSelectedWorkerServices] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [image, setImage] = useState<{
    data?: File;
    preview?: string;
  }>({});
  const [activeSection, setActiveSection] = useState<string>('info');
  const {
    isMobile,
    sidebarOpen,
    setSidebarOpen
  } = useResponsive();
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(setLoading(true));

    const formData = new FormData(e.currentTarget);

    try {
      const res = await restApi(endpoints.user.basic + (paramId || '')).post({
        name: formData.get('name'),
        lastName: formData.get('lastName'),
        nationalCode: formData.get('nationalCode'),
        phoneNumber: formData.get('phoneNumber'),
        password: formData.get('password'),
        email: formData.get('email'),
        percent: formData.get('percent'),
        role: formData.get('role'),
        status: formData.get('status'),
        username: formData.get('username'),
        bankName: formData.get('bankName'),
        shebaNumber: formData.get('shebaNumber'),
        cardNumber: formData.get('cardNumber'),
        hesabNumber: formData.get('hesabNumber'),
        walletBalance: formData.get('walletBalance'),
        services: selectedWorkerServices,
        districts: selectedDistricts,
        isWorkerChoosable: user?.isWorkerChoosable,
      });

      if (res.data?.id && image.data) {
        const imageFormData = new FormData();
        imageFormData.append('file', image.data);
        await restApi(endpoints.user.medias + res.data?.id).upload(imageFormData);
      }

      if (res.code === 200) {
        Swal.fire({
          title: 'موفق',
          text: `کاربر با موفقیت ${paramId ? 'ویرایش' : 'ساخته'} شد`,
          icon: 'success',
          confirmButtonText: 'متوجه شدم',
          didClose() {
            navigate(-1);
          }
        });
      } else {
        Swal.fire({
          title: 'ناموفق',
          text: res?.data,
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'خطا',
        text: 'خطایی در ارسال اطلاعات رخ داد',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchData = async () => {
    dispatch(setLoading(true));

    try {
      const [servicesRes, districtsRes, userRes] = await Promise.all([
        restApi(endpoints.service.client).get(),
        restApi(endpoints.district.index).get(),
        paramId ? restApi(endpoints.user.single + paramId).get() : Promise.resolve(null),
      ]);

      setDistricts(districtsRes.data);

      const sortedServices: IService[] = [];
      servicesRes?.data?.forEach((service: IService) => {
        tools.extractChildren(service, sortedServices);
      });

      setServices(sortedServices);

      if (userRes && paramId) {
        setUser(userRes.data);
        setAddresses(userRes.data?.addresses || []);
        setSelectedWorkerServices(userRes.data?.services?.map((s: IService) => s.id) || []);
        setSelectedDistricts(userRes.data?.districts?.map((d: IDistrict) => d.id) || []);

        if (userRes.data?.profilePic?.url) {
          setImage({ preview: userRes.data?.profilePic?.url });
        }

        const formattedWorkerOffs = formatWorkerOffs(userRes.data?.workerOffs || []);
        setWorkerOffs(formattedWorkerOffs);
      }
    } catch (error) {
      Swal.fire({
        title: 'خطا',
        text: 'خطایی در دریافت اطلاعات رخ داد',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  useEffect(() => {
    fetchData();
  }, [paramId]);

  return (
    <main className="dashboardBody">
      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={toggleSidebar}
          aria-label="منوی کناری"
          aria-expanded={sidebarOpen}
        >
          ☰
        </button>
      )}

      {/* Sidebar Backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className={`sidebar-backdrop ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar className={`sideBarContainer ${sidebarOpen ? 'mobile-open' : ''}`}/>

      <form className="dashBoardMain main" onSubmit={handleSubmit}>
        <div className={`addInfoHeader ${isMobile ? 'mobile-header' : ''}`}>
          <button className="dashboardHeader keepRight clickable" type="submit">
            ارسال
          </button>
          <span className={isMobile ? 'mobile-title-section' : ''}>
            <h1 className="sideBarTitle">{!isMobile && 'بازگشت به صفحه کاربران'}</h1>
            <h1 className="dashBoardTitle">{paramId ? 'ویرایش' : 'ایجاد'} کاربر</h1>
          </span>
          <i
            className="backAdd clickable"
            onClick={() => navigate('/user/')}
            role="button"
            aria-label="بازگشت"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/user/')}
          ></i>
        </div>
        { isMobile &&
            <div className="mobile-sections-nav">
              {sections.map(section => (
                <button
                  key={section.id}
                  type="button"
                  className={`section-nav-btn ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="section-icon">{section.icon}</span>
                  <span className="section-title">{section.title}</span>
                </button>
              ))}
            </div>
        }
        <div className={`form-content ${isMobile ? 'mobile-content' : ''}`}>
          <UserInfoSection
            user={user}
            setUser={setUser}
            services={services}
            districts={districts}
            selectedWorkerServices={selectedWorkerServices}
            setSelectedWorkerServices={setSelectedWorkerServices}
            selectedDistricts={selectedDistricts}
            setSelectedDistricts={setSelectedDistricts}
            activeSection={activeSection}
          />
          <AddressesSection
            activeSection={activeSection}
            addresses={addresses}
            userId={paramId}
            onDelete={fetchData}
          />
          <OrdersSection
            activeSection={activeSection}
            orders={[...(user?.orders || []), ...(user?.jobs || [])]}
          />
          {user?.role === 'WORKER' && (
            <BankInfoSection
              activeSection={activeSection}
              user={user}/>
          )}
          <UserImageSection
            activeSection={activeSection}
            image={image}
            setImage={setImage}
          />
          <WorkerOffsSection
            activeSection={activeSection}
            workerOffs={workerOffs}
            userId={user?.id?.toString()}
            onDelete={fetchData}
          />
        </div>
      </form>
    </main>
  );
};

export default UserManage;
